import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { Plus, ShoppingBag, X, Trash2, TrendingUp, DollarSign, Search, CreditCard, Clock } from 'lucide-react';
import AmountInput from '../components/AmountInput';

const Purchases = () => {
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [items, setItems] = useState([{ productId: '', quantity: '', unitCost: '', sellingPrice: '' }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [purchaseType, setPurchaseType] = useState('cash');
  const [purchaseInfo, setPurchaseInfo] = useState({
    supplierName: '', supplierPhone: '', dueDate: '', installmentMonths: '3',
    paidAmount: '', referenceNumber: '', notes: ''
  });

  const purchaseTypes = [
    { value: 'cash', label: 'Cash Purchase', icon: DollarSign },
    { value: 'credit', label: 'Credit Purchase', icon: Clock },
    { value: 'installment', label: 'Installment', icon: CreditCard },
  ];

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [purchRes, prodRes] = await Promise.all([api.get('/purchases'), api.get('/products/with-stock')]);
      setPurchases(Array.isArray(purchRes.data) ? purchRes.data : []);
      setProducts(prodRes.data);
    } catch (err) { console.error(err); }
    setLoading(false);
  };

  const addItem = () => setItems([...items, { productId: '', quantity: '', unitCost: '', sellingPrice: '' }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));
  const updateItem = (i, field, value) => {
    const newItems = [...items];
    newItems[i][field] = value;
    setItems(newItems);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const formatted = items.map(i => ({
      productId: Number(i.productId),
      quantity: Number(i.quantity),
      unitCost: Number(i.unitCost),
      sellingPrice: Number(i.sellingPrice)
    }));
    try {
      const payload = { items: formatted, purchaseType, ...purchaseInfo,
        ...(purchaseInfo.paidAmount && { paidAmount: Number(purchaseInfo.paidAmount.replace(/[^0-9]/g, '')) })
      };
      const res = await api.post('/purchases', payload);
      setShowModal(false);
      setItems([{ productId: '', quantity: '', unitCost: '', sellingPrice: '' }]);
      setPurchaseType('cash');
      setPurchaseInfo({ supplierName: '', supplierPhone: '', dueDate: '', installmentMonths: '3', paidAmount: '', referenceNumber: '', notes: '' });
      alert(`Purchase recorded!\nEstimated Profit: TZS ${Number(res.data.estimatedProfit).toLocaleString()}`);
      fetchData();
    } catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this purchase? Stock will be reduced.')) return;
    try { await api.delete(`/purchases/${id}`); fetchData(); }
    catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const handleRecordPayment = async (id) => {
    const amount = prompt('Enter payment amount (TZS):');
    if (!amount) return;
    try { await api.put(`/purchases/${id}/payment`, { amount: Number(amount) }); fetchData(); }
    catch (err) { alert(err.response?.data?.message || 'Failed'); }
  };

  const formatCurrency = (v) => 'TZS ' + Number(v || 0).toLocaleString('en');

  const getPaymentBadge = (status) => {
    if (status === 'paid') return 'bg-emerald-500/20 text-emerald-400';
    if (status === 'pending') return 'bg-yellow-500/20 text-yellow-400';
    if (status === 'partial') return 'bg-blue-500/20 text-blue-400';
    return 'bg-gray-500/20 text-gray-400';
  };

  const filteredPurchases = purchases.filter(p => {
    if (!searchTerm) return true;
    const term = searchTerm.toLowerCase();
    return p.supplierName?.toLowerCase().includes(term) || p.purchaseType?.includes(term);
  });

  const totalSpent = purchases.reduce((sum, p) => sum + (Number(p.totalAmount) || 0), 0);
  const totalEstimatedProfit = purchases.reduce((sum, p) => sum + (Number(p.estimatedProfit) || 0), 0);

  if (loading) return <div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-emerald-500 border-t-transparent rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4 md:space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div><h2 className="text-xl md:text-2xl font-bold text-white">Purchases</h2><p className="text-gray-400 text-sm mt-1">Record stock purchases from suppliers</p></div>
        <button onClick={() => { setPurchaseType('cash'); setPurchaseInfo({ supplierName: '', supplierPhone: '', dueDate: '', installmentMonths: '3', paidAmount: '', referenceNumber: '', notes: '' }); setItems([{ productId: '', quantity: '', unitCost: '', sellingPrice: '' }]); setShowModal(true); }}
          className="flex items-center gap-2 bg-blue-500 text-white px-4 py-2.5 rounded-xl font-medium hover:bg-blue-600 transition text-sm shadow-lg shadow-blue-500/20"><Plus size={18} /> New Purchase</button>
      </div>

      {purchases.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Total Purchases</p><p className="text-white font-bold text-lg">{purchases.length}</p></div>
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Total Spent</p><p className="text-white font-bold text-lg">{formatCurrency(totalSpent)}</p></div>
          <div className="bg-[#0d1410] border border-white/5 rounded-xl p-3"><p className="text-gray-500 text-xs">Est. Profit</p><p className="text-emerald-400 font-bold text-lg">{formatCurrency(totalEstimatedProfit)}</p></div>
        </div>
      )}

      {purchases.length > 0 && (
        <div className="relative"><Search size={16} className="absolute left-3 top-2.5 text-gray-500" />
          <input type="text" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} placeholder="Search by supplier..." className="w-full pl-10 pr-4 py-2.5 bg-[#0d1410] border border-white/5 rounded-xl text-white text-sm focus:outline-none focus:border-blue-500/50" />
        </div>
      )}

      {filteredPurchases.length === 0 ? (
        <div className="bg-[#0d1410] border border-white/5 rounded-2xl p-12 text-center"><ShoppingBag size={48} className="text-gray-600 mx-auto mb-4" /><p className="text-gray-400 font-medium">No purchases yet</p></div>
      ) : (
        <div className="space-y-3">
          {filteredPurchases.map((p) => (
            <div key={p.id} className="bg-[#0d1410] border border-white/5 rounded-xl hover:border-white/10 transition-all overflow-hidden">
              <div className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="p-2.5 bg-blue-500/10 rounded-xl"><ShoppingBag size={20} className="text-blue-400" /></div>
                    <div>
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium text-sm capitalize">{p.purchaseType} Purchase</p>
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-medium ${getPaymentBadge(p.paymentStatus)}`}>{p.paymentStatus}</span>
                      </div>
                      <p className="text-gray-500 text-xs">{p.purchaseDate} {p.supplierName && `• ${p.supplierName}`}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="text-right">
                      <p className="text-white font-bold text-sm">{formatCurrency(p.totalAmount)}</p>
                      <p className="text-emerald-400 text-xs">Profit: {formatCurrency(p.estimatedProfit)}</p>
                    </div>
                    <div className="flex gap-1">
                      {(p.paymentStatus === 'pending' || p.paymentStatus === 'partial') && (
                        <button onClick={() => handleRecordPayment(p.id)} className="p-1.5 text-gray-500 hover:text-emerald-400 transition"><DollarSign size={14} /></button>
                      )}
                      <button onClick={() => handleDelete(p.id)} className="p-1.5 text-gray-500 hover:text-red-400 transition"><Trash2 size={14} /></button>
                    </div>
                  </div>
                </div>
                {p.items && (
                  <div className="mt-3 pt-3 border-t border-white/5 space-y-1">
                    {p.items.map((item, i) => (
                      <div key={i} className="flex justify-between text-xs"><span className="text-gray-400">{item.productName} x{item.quantity}</span><span className="text-emerald-400">Profit: {formatCurrency(item.estimatedProfit)}</span></div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-[#111a14] border border-white/10 rounded-2xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-6"><h3 className="text-white font-bold text-lg">New Purchase</h3><button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-white"><X size={20} /></button></div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm text-gray-300 mb-2">Purchase Type</label>
                <div className="grid grid-cols-3 gap-2">
                  {purchaseTypes.map(pt => (
                    <button key={pt.value} type="button" onClick={() => setPurchaseType(pt.value)}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl border text-xs transition-all ${purchaseType === pt.value ? 'bg-blue-500/10 border-blue-500/30 text-blue-400' : 'bg-[#0d1611] border-white/5 text-gray-400 hover:border-white/10'}`}>
                      <pt.icon size={18} /><span className="font-medium">{pt.label}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="bg-[#0d1611] border border-white/5 rounded-xl p-4 space-y-3">
                <p className="text-gray-400 text-xs font-medium uppercase">Supplier Details</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <input type="text" placeholder="Supplier Name" value={purchaseInfo.supplierName} onChange={e => setPurchaseInfo({...purchaseInfo, supplierName: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" />
                  <input type="text" placeholder="Supplier Phone" value={purchaseInfo.supplierPhone} onChange={e => setPurchaseInfo({...purchaseInfo, supplierPhone: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" />
                  {purchaseType === 'credit' && <input type="date" value={purchaseInfo.dueDate} onChange={e => setPurchaseInfo({...purchaseInfo, dueDate: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" />}
                  {purchaseType === 'installment' && (
                    <>
                      <input type="number" placeholder="Months" value={purchaseInfo.installmentMonths} onChange={e => setPurchaseInfo({...purchaseInfo, installmentMonths: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" />
                      <AmountInput placeholder="Down Payment (TZS)" value={purchaseInfo.paidAmount} onChange={e => setPurchaseInfo({...purchaseInfo, paidAmount: e.target.value})} className="px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" />
                    </>
                  )}
                </div>
              </div>
              {items.map((item, i) => (
                <div key={i} className="bg-[#0d1611] border border-white/5 rounded-xl p-4 space-y-3">
                  <div className="flex items-center justify-between"><span className="text-gray-400 text-xs font-medium">Item {i + 1}</span>{items.length > 1 && <button type="button" onClick={() => removeItem(i)} className="text-red-400 hover:text-red-300"><Trash2 size={14} /></button>}</div>
                  <select value={item.productId} onChange={e => updateItem(i, 'productId', e.target.value)} className="w-full px-3 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" required>
                    <option value="">Select product</option>
                    {products.map(p => <option key={p.id} value={p.id} className="bg-[#0a0f0d]">{p.name}</option>)}
                  </select>
                  <div className="grid grid-cols-3 gap-2">
                    <input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateItem(i, 'quantity', e.target.value)} className="px-2 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" required />
                    <AmountInput placeholder="Buy Price" value={item.unitCost} onChange={e => updateItem(i, 'unitCost', e.target.value)} className="px-2 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" required />
                    <AmountInput placeholder="Sell Price" value={item.sellingPrice} onChange={e => updateItem(i, 'sellingPrice', e.target.value)} className="px-2 py-2.5 bg-[#0a0f0d] border border-white/10 rounded-lg text-white text-sm focus:outline-none focus:border-blue-500/50" required />
                  </div>
                </div>
              ))}
              <button type="button" onClick={addItem} className="w-full py-2.5 border border-dashed border-white/10 rounded-xl text-gray-400 hover:text-white text-sm flex items-center justify-center gap-2"><Plus size={16} /> Add Item</button>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 py-3 border border-white/10 rounded-xl text-gray-400 hover:text-white transition text-sm">Cancel</button>
                <button type="submit" className="flex-1 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition text-sm">Record Purchase</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Purchases;
